/**
 * Keeps invisible characters out of email fields.
 *
 * An address copied from WhatsApp, a PDF, Word or an Arabic page often carries
 * hidden characters - zero-width spaces, left-to-right / right-to-left marks,
 * a byte-order mark. The address looks perfect on screen, but the browser's own
 * `type="email"` check rejects it with "A part following '@' should not contain
 * the symbol ''" (the symbol is invisible, hence the empty quotes), and a server
 * lookup by that address finds nothing.
 *
 * `installEmailInputGuard()` runs once at startup and cleans every email field
 * as the value changes - typing, pasting and autofill - so no form has to opt in
 * and a field added later is covered automatically.
 *
 * The same file is used by edunasser-frontend, edunasser-admin and
 * edunasser-consult. Keep the three copies identical.
 */

// Unicode "format" characters: zero-width space/joiners, LRM/RLM, the bidi
// embedding and isolate marks, the word joiner, BOM, soft hyphen and the Arabic
// letter mark. None are visible and none can appear in an email address.
const FORMAT_CHARS = /\p{Cf}/gu;

// Spaces that look like a normal one (no-break space, en/em spaces, the
// ideographic space, line/paragraph separators) and control characters. None
// are valid in an address. Written as Unicode categories rather than escapes
// so the source has no invisible characters of its own. A plain space is kept
// (see sanitizeEmail): silently deleting it could turn a typo into a
// different, valid address.
const LOOKALIKE_SPACES_AND_CONTROLS = /[\p{Cc}\p{Zs}\p{Zl}\p{Zp}]/gu;

/** Removes only invisible format characters, leaving spaces and newlines alone. */
export function stripInvisibleChars(value: string): string {
  return value.replace(FORMAT_CHARS, '');
}

/** A single email address, cleaned: invisible characters removed, edges trimmed. */
export function sanitizeEmail(value: string): string {
  return stripInvisibleChars(value)
    .replace(LOOKALIKE_SPACES_AND_CONTROLS, (character) => (character === ' ' ? character : ''))
    .trim();
}

const isEmailField = (target: EventTarget | null): target is HTMLInputElement =>
  target instanceof HTMLInputElement &&
  (target.type === 'email' ||
    target.inputMode === 'email' ||
    target.autocomplete === 'email' ||
    target.name === 'email');

// The value property React watches is defined on the element itself. Writing
// through the prototype's setter bypasses it, so React still sees the value as
// changed and fires onChange with the cleaned text. A plain `el.value = x` would
// make React think nothing happened and leave its state holding the dirty value.
const nativeValueSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;

function cleanEmailField(event: Event): void {
  const field = event.target;
  if (!isEmailField(field) || !nativeValueSetter) return;

  const before = field.value;
  const after = sanitizeEmail(before);
  if (after === before) return;

  // type="email" does not expose a caret; other input types do, so keep the
  // caret where the user was instead of jumping to the end.
  let caret: number | null = null;
  try {
    caret = field.selectionStart;
  } catch {
    caret = null;
  }

  nativeValueSetter.call(field, after);

  if (caret !== null) {
    try {
      const position = sanitizeEmail(before.slice(0, caret)).length;
      field.setSelectionRange(position, position);
    } catch {
      // No selection support on this input type; nothing to restore.
    }
  }
}

let installed = false;

export function installEmailInputGuard(): void {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  // Capture phase on the document runs before React's own listeners, so the
  // component's onChange already receives the cleaned value. "change" covers
  // browsers that autofill without an input event.
  document.addEventListener('input', cleanEmailField, true);
  document.addEventListener('change', cleanEmailField, true);
}
